import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFrontChatControl } from '@features/front-chat';
import { useCan } from '@features/user';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Alert, Box, Button, Typography } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import { TemplateCatalogContainer } from './TemplateCatalogContainer';

/**
 * Full-height route for the note-template catalog (GDN-2482): /templates/catalog.
 * A data-dense workbench wants a bounded, full-width viewport, so this route
 * lives OUTSIDE the templates management layout, with a back link to it. The shell supplies the available height so the catalog's flex column
 * can pin its footer below navigation and any dynamic banner.
 *
 * No auto-redirect on a false gate: Statsig may still be initializing on a hard
 * reload, and bouncing a legitimate user is worse than a static fallback with a
 * way back.
 */
export const TemplateCatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const isEnabled = useFeatureGate(FeatureGates.PRACTICE_TEMPLATE_CATALOG);
  const canManageTemplates = useCan('manage_templates');
  const { hideLauncher, showLauncher } = useFrontChatControl();

  // The Front chat FAB sits bottom-left — exactly over the footer's selection
  // summary. Hide it for the workbench, restore on leave.
  useEffect(() => {
    hideLauncher();
    return (): void => {
      showLauncher();
    };
  }, [hideLauncher, showLauncher]);

  const handleBack = useCallback((): void => {
    navigate('/templates');
  }, [navigate]);

  if (!isEnabled || !canManageTemplates) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleBack}>
              Back
            </Button>
          }
        >
          {canManageTemplates
            ? 'The note-template catalog is not enabled for your practice.'
            : 'You do not have permission to manage note templates.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      id="template-catalog-workspace"
      sx={{
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 3, pt: 2, pb: 2, flexShrink: 0 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ ml: -1 }}
        >
          Note templates
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
          Note-template catalog
        </Typography>
      </Box>

      <Box sx={{ flex: '1 1 auto', minHeight: 0, px: 3, pb: 2.5 }}>
        <TemplateCatalogContainer />
      </Box>
    </Box>
  );
};

export default TemplateCatalogPage;
